import {
  Component,
  Input,
  Output,
  EventEmitter,
  computed,
  signal,
  inject,
  effect,
} from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { AssignmentStatus } from '../../../user/interfaces/assignment.interface';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { UserService } from '../../../user/service/user.service';
import {
  recurrenceIcon,
  recurrenceLabelKey,
} from '../../models/duty-recurrence.model';
import { FamilyDutyFeatureService } from '../../services/family-duty-feature.service';
import type { DutyRoutineProgress } from '../../helpers/duty-routine-group.helper';

@Component({
  selector: 'app-assignment-card',
  standalone: true,
  imports: [DatePipe, DecimalPipe, TranslatePipe, ParentButtonComponent],
  templateUrl: './assignment-card.component.html',
  styleUrl: './assignment-card.component.scss',
})
export class AssignmentCardComponent {
  #userService = inject(UserService);
  #familyDuty = inject(FamilyDutyFeatureService);

  @Input() title = '';
  @Input() description = '';
  @Input() status: AssignmentStatus = 'new';
  @Input() isSelected = false;
  @Input() id = '';
  @Input() rewardGold = 0;
  @Input() rewardXp = 0;
  @Input() read = false;
  @Input() isOverdue = false;
  @Input() creationDate = 0;
  @Input() completionDate = 0;
  @Input() confirmationDate = 0;
  @Input() completionNotes = '';
  @Input() recurrenceFrequency: string | null | undefined = null;
  @Input() requiresParentParticipation: boolean | undefined = false;
  @Input() parentNote: string | null | undefined = null;
  @Input() parentCompleted: boolean | undefined = false;
  @Input() routineProgress: DutyRoutineProgress | null = null;

  @Output() cardSelected = new EventEmitter<void>();
  @Output() editRequested = new EventEmitter<void>();

  isLoading = signal(false);
  isParentPartLoading = signal(false);
  userIsLoading = computed(() => this.#userService.isLoading());

  readonly isLoadingSig = computed(
    () => this.isLoading() || this.userIsLoading(),
  );

  readonly recurrenceLabelKey = computed(() =>
    recurrenceLabelKey(this.recurrenceFrequency),
  );
  readonly recurrenceIconSrc = computed(() =>
    recurrenceIcon(this.recurrenceFrequency),
  );

  get hasRewards(): boolean {
    return this.rewardGold > 0 || this.rewardXp > 0;
  }

  get showKidNote(): boolean {
    return this.status === 'marked' && !!this.completionNotes?.trim();
  }

  get isSelectable(): boolean {
    return this.status === 'marked';
  }

  get isEditable(): boolean {
    return this.status === 'new';
  }

  get showFamilyDuty(): boolean {
    return this.#familyDuty.isEnabled() && !!this.requiresParentParticipation;
  }

  get showParentNote(): boolean {
    return !!this.parentNote?.trim();
  }

  get showParentPartAction(): boolean {
    return (
      this.showFamilyDuty &&
      this.status === 'new' &&
      !this.parentCompleted
    );
  }

  constructor() {
    effect(() => {
      if (!this.userIsLoading()) {
        this.isLoading.set(false);
        this.isParentPartLoading.set(false);
      }
    });
  }

  onCardClick(): void {
    this.cardSelected.emit();
  }

  onEditClick(event: Event): void {
    event.stopPropagation();
    this.editRequested.emit();
  }

  onDoneButtonClick(event: Event): void {
    event.stopPropagation();
    this.isLoading.set(true);
    this.#userService.confirmDuty.mutate(this.id);
  }

  onParentPartClick(event: Event): void {
    event.stopPropagation();
    this.isParentPartLoading.set(true);
    this.#userService.completeParentDutyPart.mutate(this.id);
  }
}
